import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCents, useAccounts } from "@/lib/money";
import api from "@/lib/api";
import { toast } from "sonner";
import { CreditCard } from "lucide-react";

/**
 * Prompt shown when the user marks a bill/renewal item done.
 * Confirms → creates a linked transaction and advances the recurring rule.
 * Skip → just marks the item done without recording payment.
 */
export default function PaymentConfirmDialog({ item, open, onOpenChange, onDone }) {
  const { accounts } = useAccounts();
  const [accountId, setAccountId] = useState("");
  const [amountCents, setAmountCents] = useState(null);
  const [rule, setRule] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !item?.linked_recurring_id) return;
    (async () => {
      const r = await api.get("/money/recurring");
      const found = r.data.find((x) => x.id === item.linked_recurring_id);
      if (found) {
        setRule(found);
        setAmountCents(found.amount_cents);
        setAccountId(found.account_id);
      }
    })();
  }, [open, item]);

  if (!item) return null;

  const skip = async () => {
    setSaving(true);
    try {
      await api.patch(`/items/${item.id}`, { status: "done" });
      onDone?.({ paid: false });
      onOpenChange(false);
      toast.success("Marked done");
    } finally { setSaving(false); }
  };

  const confirm = async () => {
    if (!accountId) return toast.error("Pick an account");
    setSaving(true);
    try {
      const r = await api.post(`/items/${item.id}/pay`, { account_id: accountId });
      onDone?.({ paid: true, transaction_id: r.data.transaction_id });
      onOpenChange(false);
      toast.success(`Recorded ${formatCents(r.data.amount_cents)} payment`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-surface rounded-hero border-none p-6" data-testid="payment-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="text-surface-on text-lg font-medium">Record payment?</DialogTitle>
        </DialogHeader>
        <div className="mt-3">
          <div className="text-sm text-surface-onVariant">{item.title}</div>
          {amountCents !== null && (
            <div className="mt-1 text-3xl font-medium text-surface-on tracking-tight tabular-nums" data-testid="payment-amount">
              {formatCents(amountCents)}
            </div>
          )}
          {rule && (
            <div className="mt-1 text-xs text-surface-onVariant">
              Linked to recurring · {rule.frequency}
            </div>
          )}
        </div>
        {amountCents !== null && (
          <div className="mt-4">
            <div className="text-xs font-medium tracking-widest uppercase text-surface-onVariant mb-1">Pay from</div>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="w-full rounded-t-lg rounded-b-none bg-surface-containerHigh border-b-2 border-outline focus:border-primary h-11" data-testid="payment-account-select">
                <SelectValue placeholder="Choose account" />
              </SelectTrigger>
              <SelectContent className="rounded-2xl bg-surface">
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    <span className="inline-flex items-center gap-2">
                      <CreditCard className="w-3.5 h-3.5 text-surface-onVariant" />
                      {a.name} · {formatCents(a.current_balance_cents, a.currency)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-6">
          <button
            data-testid="payment-skip"
            onClick={skip}
            disabled={saving}
            className="px-5 py-2.5 rounded-full text-sm font-medium text-surface-on hover:bg-primary/8 transition-colors duration-200 ease-md"
          >Skip — just mark done</button>
          {amountCents !== null && (
            <button
              data-testid="payment-confirm"
              onClick={confirm}
              disabled={saving || !accountId}
              className="px-5 py-2.5 rounded-full text-sm font-medium bg-primary text-primary-foreground active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md"
            >{saving ? "Recording…" : "Record payment"}</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
